'use client';

import { cn } from '@cobliteam/ui/utils';
import type { CSSProperties, HTMLAttributes, MouseEvent } from 'react';
import { forwardRef, memo, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import styles from './AIIcon.module.css';

export interface AIIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

// Backward compatibility alias
const SPARKLE_PATH =
  'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z';

function SparkleSvg({ className, gradientUrl }: { className?: string; gradientUrl?: string }) {
  const strokeFill = gradientUrl ?? 'currentColor';
  const svgStyle = gradientUrl ? ({ fill: strokeFill, stroke: strokeFill } as CSSProperties) : undefined;
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      viewBox='0 0 24 24'
      style={svgStyle}
      strokeLinecap='round'
      strokeLinejoin='round'
      shapeRendering='geometricPrecision'
      vectorEffect='non-scaling-stroke'
      aria-hidden
    >
      <path d={SPARKLE_PATH} />
    </svg>
  );
}

function SparkleLayer({ gradientUrl }: { gradientUrl?: string }) {
  return (
    <>
      <span className={styles.centerWrap}>
        <span className={styles.centerStar}>
          <SparkleSvg gradientUrl={gradientUrl} />
        </span>
      </span>
      <span className={cn(styles.star, styles.trStar)}>
        <SparkleSvg gradientUrl={gradientUrl} />
      </span>
      <span className={cn(styles.star, styles.blStar)}>
        <SparkleSvg gradientUrl={gradientUrl} />
      </span>
    </>
  );
}

function SparkleGradientDefs({ id }: { id: string }) {
  return (
    <svg aria-hidden className='pointer-events-none absolute h-0 w-0' xmlns='http://www.w3.org/2000/svg'>
      <defs>
        <linearGradient id={id} x1='0%' y1='0%' x2='100%' y2='0%'>
          <stop offset='0%' stopColor='#7c3aed' />
          <stop offset='15%' stopColor='#6d28d9' />
          <stop offset='65%' stopColor='#0248e9' />
          <stop offset='100%' stopColor='#327af6' />
        </linearGradient>
      </defs>
    </svg>
  );
}

function AnimSparklesBlock({
  continuous,
  frozen,
  replayKey,
  animLayerClass,
  gradientUrl,
}: {
  continuous: boolean;
  frozen: boolean;
  replayKey: number;
  animLayerClass: string;
  gradientUrl?: string;
}) {
  const inner = <SparkleLayer gradientUrl={gradientUrl} />;
  if (continuous || frozen) {
    return <div className={animLayerClass}>{inner}</div>;
  }
  return (
    <div key={replayKey} className={animLayerClass}>
      {inner}
    </div>
  );
}

function useSparkleGradient(sparklesPaint: 'white' | 'brand-gradient') {
  const rawId = useId();
  const sparkleGradientId = `ai-sparkle-${rawId.replace(/\W/g, '')}`;
  const sparklesGradientUrl = sparklesPaint === 'brand-gradient' ? `url(#${sparkleGradientId})` : undefined;
  return { sparkleGradientId, sparklesGradientUrl };
}

export type AIIconVariant = 'solid' | 'soft' | 'ghost';
export type AIIconShape = 'circle' | 'rounded' | 'square';
export type AIIconSizeToken = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';

// ─── Container System ──────────────────────────────────────────────────────────

const CONTAINER_PX: Record<AIIconSizeToken, number> = { sm: 32, md: 40, lg: 48, xl: 56, '2xl': 64, '3xl': 72, '4xl': 80, '5xl': 120 };

const SOFT_BACKGROUND_STYLE: CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), linear-gradient(248deg, #8b5cf6 20%, #0248e9 60%, #001bb8 100%)',
};

const containerVariants = tv({
  base: 'relative inline-flex flex-shrink-0 items-center justify-center overflow-hidden',
  variants: {
    variant: {
      solid: 'bg-[linear-gradient(248deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)] text-white shadow-[1px_5px_11px_0px_rgba(139,92,246,0.32)]',
      soft: 'text-gray-12',
      ghost: 'bg-transparent text-current',
    },
    shape: {
      circle: 'rounded-full',
      rounded: '',
      square: 'rounded-none',
    },
    iconSize: {
      sm: 'size-8 rounded-lg',
      md: 'size-10 rounded-[10px]',
      lg: 'size-12 rounded-xl',
      xl: 'size-14 rounded-xl',
      '2xl': 'size-16 rounded-xl',
      '3xl': 'size-[72px] rounded-2xl',
      '4xl': 'size-20 rounded-2xl',
      '5xl': 'size-[120px] rounded-[1.75rem]',
    },
  },
  compoundVariants: [
    { shape: 'circle', class: 'rounded-full' },
    { shape: 'square', class: 'rounded-none' },
  ],
  defaultVariants: { shape: 'rounded' },
});

function getEffectiveSparklesPaint(variant: AIIconVariant | undefined, sparklesPaint: 'white' | 'brand-gradient'): 'white' | 'brand-gradient' {
  if (variant === 'solid') return 'white';
  if (variant === 'soft' || variant === 'ghost') return 'brand-gradient';
  return sparklesPaint;
}

export type AIIconProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Numeric pixel size for standalone sparkle-only use (no container background).
   * Use `iconSize` to activate the variant/shape container system.
   * @default 28
   */
  size?: number;
  /** Preset size token — activates container mode. Pairs with `variant` and `shape`. */
  iconSize?: AIIconSizeToken;
  /** Container background style. Requires `iconSize`. `solid`: gradient bg + white sparkles. `soft`: frosted gradient bg. `ghost`: no bg. */
  variant?: AIIconVariant;
  /** Border-radius shape. Requires `iconSize`. @default 'rounded' */
  shape?: AIIconShape;
  /**
   * When the parent row becomes hovered (e.g. sidebar), replays the one-shot animation.
   * Does not apply when `animationActive` is set.
   */
  isHovered?: boolean;
  /**
   * `true`: loop continuously (gradient header). `false`: frozen. `undefined`: one play on mount, replay on hover.
   */
  animationActive?: boolean;
  /** `white`: sparkles match solid / on-surface. `brand-gradient`: Cobli AI foreground gradient (#7c3aed → #327af6) for soft variant. */
  sparklesPaint?: 'white' | 'brand-gradient';
};

// Backward compatibility aliases
export type AIAssistantIconVariant = AIIconVariant;
export type AIAssistantIconShape = AIIconShape;
export type AIAssistantIconSizeToken = AIIconSizeToken;

// Backward compatibility alias
export type AIAssistantIconProps = AIIconProps;

const AIIcon = forwardRef<AIIconHandle, AIIconProps>(
  ({ className, size = 28, iconSize, variant, shape, isHovered = false, animationActive, sparklesPaint = 'brand-gradient', style, onMouseEnter, ...props }, ref) => {
    const effectivePaint = getEffectiveSparklesPaint(variant, sparklesPaint);
    const { sparkleGradientId, sparklesGradientUrl } = useSparkleGradient(effectivePaint);
    const continuous = animationActive === true;
    const propFrozen = animationActive === false;

    const [replayKey, setReplayKey] = useState(0);
    const [refStopped, setRefStopped] = useState(false);
    const prevPropHovered = useRef(isHovered);
    const didMount = useRef(false);

    const bumpReplay = () => {
      if (continuous || propFrozen || refStopped) return;
      setReplayKey((k) => k + 1);
    };

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () => {
          setRefStopped(false);
          if (!continuous) {
            setReplayKey((k) => k + 1);
          }
        },
        stopAnimation: () => setRefStopped(true),
      }),
      [continuous]
    );

    useEffect(() => {
      if (continuous || propFrozen) return;
      if (!didMount.current) {
        didMount.current = true;
        prevPropHovered.current = isHovered;
        return;
      }
      if (isHovered && !prevPropHovered.current) {
        setReplayKey((k) => k + 1);
      }
      prevPropHovered.current = isHovered;
    }, [continuous, propFrozen, isHovered]);

    const frozen = propFrozen || refStopped;
    const animLayerClass = cn(styles.animLayer, continuous && styles.animContinuous, frozen && styles.staticDecor);

    const handleMouseEnter = (e: MouseEvent<HTMLDivElement>) => {
      bumpReplay();
      onMouseEnter?.(e);
    };

    const sparkles = <AnimSparklesBlock animLayerClass={animLayerClass} continuous={continuous} frozen={frozen} gradientUrl={sparklesGradientUrl} replayKey={replayKey} />;

    const sparklesDefs = sparklesGradientUrl ? <SparkleGradientDefs id={sparkleGradientId} /> : null;

    // ─── Container mode (iconSize token provided) ──────────────────────────────
    if (iconSize) {
      const containerPx = CONTAINER_PX[iconSize];
      const containerStyle: CSSProperties = {
        width: containerPx,
        height: containerPx,
        ...(variant === 'soft' ? SOFT_BACKGROUND_STYLE : {}),
        ...style,
      };

      return (
        <div className={cn(containerVariants({ variant, shape, iconSize }), className)} style={containerStyle} onMouseEnter={handleMouseEnter} {...props}>
          {sparklesDefs}
          {sparkles}
        </div>
      );
    }

    // ─── Legacy standalone sparkle mode (numeric size) ─────────────────────────
    return (
      <div className={cn(styles.root, className)} style={{ width: size, height: size, ...style }} onMouseEnter={handleMouseEnter} {...props}>
        {sparklesDefs}
        {sparkles}
      </div>
    );
  }
);

AIIcon.displayName = 'AIIcon';

export default memo(AIIcon);
